import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import { isNotNullOrUndefined } from '../../Util/TypeGuards';

export interface ICapacityTypeEntityAttributes extends IModelAttributes {
	label: string;
	description: string;
	abbreviation: string;
	iconName: string;
	order?: number;
	hideCapacity: boolean;
	hideCapacityOnTripCards: boolean;
	disabled: boolean;

	measurements: Array<Models.CapacityTypeMeasurement | Models.ICapacityTypeMeasurementAttributes>;

	capacityRestrictions: Array<Models.CapacityRestrictionEntity | Models.ICapacityRestrictionEntityAttributes>;

	cargoCharacteristics: Array<
		Models.CapacityTypeCargoCharacteristic | Models.ICapacityTypeCargoCharacteristicAttributes
	>;

	additionalBookingOptionId: string;
	additionalBookingOption: Models.AdditionalBookingOptionEntity | Models.IAdditionalBookingOptionEntityAttributes;
}

@entity('CapacityTypeEntity', 'Capacity Type')
export default class CapacityTypeEntity extends Model implements ICapacityTypeEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<CapacityTypeEntity, string>()
	public label: string;

	@observable
	@attribute<CapacityTypeEntity, string>()
	public description: string;

	@observable
	@attribute<CapacityTypeEntity, string>()
	public abbreviation: string;

	@observable
	@attribute<CapacityTypeEntity, string>()
	public iconName: string;

	@observable
	@attribute<CapacityTypeEntity, number>()
	public order: number;

	@observable
	@attribute<CapacityTypeEntity, boolean>()
	public hideCapacity: boolean;

	@observable
	@attribute<CapacityTypeEntity, boolean>()
	public hideCapacityOnTripCards: boolean;

	@observable
	@attribute<CapacityTypeEntity, boolean>()
	public disabled: boolean;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public cargoCharacteristics: Models.CapacityTypeCargoCharacteristic[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public measurements: Models.CapacityTypeMeasurement[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public capacityRestrictions: Models.CapacityRestrictionEntity[] = [];

	@observable
	@attribute()
	public additionalBookingOptionId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public additionalBookingOption: Models.AdditionalBookingOptionEntity;

	constructor(attributes?: Partial<ICapacityTypeEntityAttributes>) {
		super(attributes);

		this.measurements = [];
		this.capacityRestrictions = [];
		this.cargoCharacteristics = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<ICapacityTypeEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (isNotNullOrUndefined(attributes.label)) {
				this.label = attributes.label;
			}
			if (isNotNullOrUndefined(attributes.description)) {
				this.description = attributes.description;
			}
			if (isNotNullOrUndefined(attributes.abbreviation)) {
				this.abbreviation = attributes.abbreviation;
			}
			if (isNotNullOrUndefined(attributes.iconName)) {
				this.iconName = attributes.iconName;
			}
			if (isNotNullOrUndefined(attributes.order)) {
				this.order = attributes.order;
			}
			if (isNotNullOrUndefined(attributes.hideCapacity)) {
				this.hideCapacity = attributes.hideCapacity;
			}
			if (isNotNullOrUndefined(attributes.hideCapacityOnTripCards)) {
				this.hideCapacityOnTripCards = attributes.hideCapacityOnTripCards;
			}
			if (isNotNullOrUndefined(attributes.disabled)) {
				this.disabled = attributes.disabled;
			}
			if (attributes.cargoCharacteristics !== undefined && Array.isArray(attributes.cargoCharacteristics)) {
				this.cargoCharacteristics = [];
				for (const model of attributes.cargoCharacteristics) {
					if (model instanceof Models.CapacityTypeCargoCharacteristic) {
						this.cargoCharacteristics.push(model);
					} else {
						this.cargoCharacteristics.push(new Models.CapacityTypeCargoCharacteristic(model));
					}
				}
			}
			if (attributes.measurements !== undefined && Array.isArray(attributes.measurements)) {
				this.measurements = [];
				for (const model of attributes.measurements) {
					if (model instanceof Models.CapacityTypeMeasurement) {
						this.measurements.push(model);
					} else {
						this.measurements.push(new Models.CapacityTypeMeasurement(model));
					}
				}
			}
			if (isNotNullOrUndefined(attributes.capacityRestrictions)) {
				for (const model of attributes.capacityRestrictions) {
					if (model instanceof Models.CapacityRestrictionEntity) {
						this.capacityRestrictions.push(model);
					} else {
						this.capacityRestrictions.push(new Models.CapacityRestrictionEntity(model));
					}
				}
			}
			if (isNotNullOrUndefined(attributes.additionalBookingOptionId)) {
				this.additionalBookingOptionId = attributes.additionalBookingOptionId;
			}
			if (isNotNullOrUndefined(attributes.additionalBookingOption)) {
				if (attributes.additionalBookingOption === null) {
					this.additionalBookingOption = attributes.additionalBookingOption;
				} else if (attributes.additionalBookingOption instanceof Models.AdditionalBookingOptionEntity) {
					this.additionalBookingOption = attributes.additionalBookingOption;
					this.additionalBookingOptionId = attributes.additionalBookingOption.id;
				} else {
					this.additionalBookingOption = new Models.AdditionalBookingOptionEntity(
						attributes.additionalBookingOption,
					);
					this.additionalBookingOptionId = this.additionalBookingOption.id;
				}
			}
		}
	}
}
